// src/services/authService.js
import { 
  onAuthStateChanged, 
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence,
  sendPasswordResetEmail,
  updatePassword
} from "firebase/auth";
import { auth } from "./firebase.js";
import { logger } from "../utils/logger";

/**
 * @file This service provides functions for handling user authentication,
 * including login, logout, signup, and session management.
 */

// Helper to measure performance of async operations
const measureAuthPerformance = async (operationName, promiseFn) => {
  const start = performance.now();
  try {
    const result = await promiseFn();
    const end = performance.now();
    logger.log(`authService: ${operationName} completed in ${(end - start).toFixed(2)} ms`);
    return result;
  } catch (error) {
    const end = performance.now();
    logger.error(`authService: ${operationName} failed in ${(end - start).toFixed(2)} ms`, error);
    throw error;
  }
};

/**
 * Registers a callback to be called when the user's authentication state changes.
 *
 * @param {Function} callback - The function to call when the auth state changes.
 * @returns {Function} An unsubscribe function.
 */
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Logs the user out.
 *
 * @returns {Promise<void>} A promise that resolves when the user is logged out.
 */
export const logout = () => {
  logger.log("authService: Logging out user");
  return measureAuthPerformance("logout", () => signOut(auth));
};

/**
 * Logs the user in with email and password.
 *
 * @param {string} email - The user's email.
 * @param {string} password - The user's password.
 * @returns {Promise<UserCredential>} A promise that resolves with the user credential.
 */
export const loginWithEmail = (email, password) => {
  return measureAuthPerformance("loginWithEmail", () => 
    signInWithEmailAndPassword(auth, email, password)
  );
};

/**
 * Signs the user up with email and password.
 *
 * @param {string} email - The user's email.
 * @param {string} password - The user's password.
 * @returns {Promise<UserCredential>} A promise that resolves with the user credential.
 */
export const signupWithEmail = (email, password) => {
  return measureAuthPerformance("signupWithEmail", () =>
    createUserWithEmailAndPassword(auth, email, password)
  );
};

/**
 * Logs the user in with Google.
 *
 * @returns {Promise<{user: User, isNewUser: boolean}>} A promise that resolves with the user object and a flag indicating if the user is new.
 */
export const loginWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  return measureAuthPerformance("loginWithGoogle", async () => {
    const result = await signInWithPopup(auth, provider);
    const additionalUserInfo = getAdditionalUserInfo(result);
    return { user: result.user, isNewUser: additionalUserInfo?.isNewUser };
  });
};

export const loginWithMicrosoft = async () => {
  const provider = new OAuthProvider('microsoft.com'); // Correct: Instantiate OAuthProvider with the Microsoft ID
  return measureAuthPerformance("loginWithMicrosoft", async () => {
    const result = await signInWithPopup(auth, provider);
    const additionalUserInfo = getAdditionalUserInfo(result);
    return { user: result.user, isNewUser: additionalUserInfo?.isNewUser };
  });
};

/**
 * Logs the user in with Facebook.
 *
 * @returns {Promise<{user: User, isNewUser: boolean}>} A promise that resolves with the user object and a flag indicating if the user is new.
 */
export const loginWithFacebook = async () => {
  const provider = new FacebookAuthProvider();
  return measureAuthPerformance("loginWithFacebook", async () => {
    const result = await signInWithPopup(auth, provider);
    const additionalUserInfo = getAdditionalUserInfo(result);
    return { user: result.user, isNewUser: additionalUserInfo?.isNewUser };
  });
};

/**
 * Sets the authentication persistence type.
 *
 * @param {boolean} keepLoggedIn - A flag indicating if the user should be kept logged in.
 * @returns {Promise<void>} A promise that resolves when the persistence is set.
 */
export const setAuthPersistence = (keepLoggedIn) => {
  const persistenceType = keepLoggedIn
    ? browserLocalPersistence
    : browserSessionPersistence;
  return measureAuthPerformance("setAuthPersistence", () => 
    setPersistence(auth, persistenceType)
  );
};

/**
 * Sends a password reset email to the user.
 *
 * @param {string} email - The user's email.
 * @returns {Promise<void>} A promise that resolves when the email is sent.
 */
export const sendPasswordReset = (email) => {
  return measureAuthPerformance("sendPasswordReset", () =>
    sendPasswordResetEmail(auth, email)
  );
};

/**
 * Changes the user's password.
 *
 * @param {string} newPassword - The new password.
 * @returns {Promise<void>} A promise that resolves when the password is changed.
 */
export const changePassword = (newPassword) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("No user is signed in to change the password.");
  }
  return measureAuthPerformance("changePassword", () =>
    updatePassword(user, newPassword)
  );
};

/**
 * Gets the user's admin status.
 *
 * @param {User} user - The user object.
 * @returns {Promise<boolean>} A promise that resolves with a boolean indicating if the user is an admin.
 */
export const getUserAdminStatus = async (user) => {
  if (!user) {
    logger.log("authService: getUserAdminStatus called with no user, returning false.");
    return false;
  }
  return measureAuthPerformance("getUserAdminStatus", async () => {
    try {
      const idTokenResult = await user.getIdTokenResult();
      const isAdmin = idTokenResult.claims.admin === true;
      logger.log(`authService: User admin status for ${user.uid} is ${isAdmin}`);
      return isAdmin;
    } catch (error) {
      logger.error("authService: Error getting ID token result:", error);
      return false;
    }
  });
};

/**
 * Refreshes the user's ID token.
 *
 * @param {User} user - The user object.
 * @returns {Promise<void>} A promise that resolves when the token is refreshed.
 */
export const refreshToken = (user) => {
  if (!user) return Promise.resolve();
  return measureAuthPerformance("refreshToken", () => user.getIdToken(true)); // Force refresh
};

/**
 * Checks if current user session needs refresh and performs it if necessary.
 * - This helps recover Firebase Auth state after app has been in background
 * - Particularly useful for iOS Safari which can terminate background tabs
 * @param {number} inactiveTimeMs How long the app was inactive (optional)
 * @returns {Promise<boolean>} True if refresh was needed and successful
 */
export const checkAndRefreshSession = async (inactiveTimeMs = 0) => {
  return measureAuthPerformance("checkAndRefreshSession", async () => {
    // Get current user
    const currentUser = auth.currentUser;
    
    // If no user or token, nothing to refresh
    if (!currentUser) {
      logger.log("authService: No current user to refresh session for");
      return false;
    }
    
    try {
      // For mobile devices, use a more conservative approach with shorter timeouts
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      // Add timeout to prevent hanging
      const timeoutDuration = isMobile ? 4000 : 8000; // Shorter timeout for mobile
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Token refresh timeout")), timeoutDuration);
      });
      
      // Force token refresh if app was in background for more than 10 minutes
      // or if we're on mobile and inactive for more than 5 minutes
      const forceRefresh = inactiveTimeMs > (isMobile ? 5 : 10) * 60 * 1000;
      
      // Create the token fetch promise
      const tokenPromise = async () => {
        // Get token with force refresh if needed
        const token = await currentUser.getIdToken(forceRefresh);
        
        // Parse the token to check its expiration time
        const tokenData = JSON.parse(atob(token.split(".")[1]));
        const expirationTime = tokenData.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const timeToExpiry = expirationTime - currentTime;
        
        logger.log(`authService: Token expires in ${(timeToExpiry / 60000).toFixed(1)} minutes`);
        
        // If token is about to expire (less than 5 minutes), force refresh
        if (timeToExpiry < 5 * 60 * 1000) {
          logger.log("authService: Token about to expire, forcing refresh");
          await currentUser.getIdToken(true);
        }
        
        return true;
      };
      
      // Race the token fetch with the timeout
      return await Promise.race([tokenPromise(), timeoutPromise]);
    } catch (error) {
      logger.error("authService: Error refreshing session:", error);
      
      // For certain Firebase errors that indicate session expiry,
      // return a special code that can be handled by the caller
      if (
        error.code === 'auth/user-token-expired' ||
        error.code === 'auth/requires-recent-login' ||
        error.message?.includes('token expired')
      ) {
        logger.warn("authService: Token expired, session needs full recovery");
        return { needsReAuth: true };
      }
      
      return false;
    }
  });
};
